import { useSanitizedRouteParams } from 'src/hooks/useSanitizedRouteParams';
import { createContext, ReactNode, useContext, useEffect } from 'react';

import { Form } from 'antd';
import { FormProvider, Resolver, useForm, UseFormReturn } from 'react-hook-form';

import { useClient } from 'src/api/axios/config';
import {
  FanguardApiContractsClientAssetModel,
  FanguardApiContractsClientClientCustomPersonFieldModel,
  FanguardApiContractsClientIConnectionModel,
  FanguardApiContractsClientSiteDetailModel,
} from 'src/api/generated';
import { useGetClient, useGetClientAssets } from 'src/hooks/api/useClientsEndpoint';
import { useGetSite, useUpdateSite } from 'src/hooks/api/useSiteEndpoint';
import { useAppNavigation } from 'src/routes';
import { extractDirtyValues } from 'src/utils/helpers';

import { yupResolver } from '@hookform/resolvers/yup';

import { SiteFormValues, siteSchema } from './config';

interface SiteDetailsContextType {
  isLoading: boolean;
  error: Error | null;
  site?: FanguardApiContractsClientSiteDetailModel;
  formMethods: UseFormReturn<SiteFormValues>;
  clientConnections: FanguardApiContractsClientIConnectionModel[];
  clientCustomPersonFields: FanguardApiContractsClientClientCustomPersonFieldModel[];
  authenticationConnections: FanguardApiContractsClientSiteDetailModel['connections'];
  updateSite: ReturnType<typeof useUpdateSite>['mutate'];
  isUpdateSitePending: boolean;
  handleSave: () => Promise<void> | undefined;
  clientAssets: FanguardApiContractsClientAssetModel[];
  isClientAssetsLoading: boolean;
  handleCancel: () => void;
  handleNavigateBack: () => void;
  handleUpdateName: (name: string) => void;
  handleTabChange: (key: string) => void;
}

const SiteDetailsContext = createContext<SiteDetailsContextType | undefined>(undefined);

/** The label is left out of the form because the checklist reads labels straight from the client's custom person
 * fields, and the API resolves them again on read. */
const toPersonFieldFormValues = (site: FanguardApiContractsClientSiteDetailModel): SiteFormValues['personFields'] =>
  site.personFields?.map((personField) => ({
    type: personField.type ?? 'Custom',
    key: personField.key ?? undefined,
    required: personField.required ?? false,
  })) ?? [];

export const useSiteDetails = () => {
  const context = useContext(SiteDetailsContext);
  if (!context) {
    throw new Error('useSiteDetails must be used within a SiteDetailsProvider');
  }
  return context;
};

interface SiteDetailsProviderProps {
  children: ReactNode;
}

export const SiteDetailsProvider = ({ children }: SiteDetailsProviderProps) => {
  const { id } = useSanitizedRouteParams();
  const { data: site, isLoading, error } = useGetSite(id);
  const client = useClient();
  const { data: clientData } = useGetClient(client);
  const { data: clientAssets, isLoading: isClientAssetsLoading } = useGetClientAssets(client);
  const { mutate: updateSite, isPending: isUpdateSitePending } = useUpdateSite(id ?? '');
  const { navigateTo } = useAppNavigation();
  const clientConnections = clientData?.connections ?? [];
  const clientCustomPersonFields = clientData?.customPersonFields ?? [];
  const authenticationConnections = clientConnections.filter(
    (connection) => connection.connectionType === 'Authentication',
  );

  const formMethods = useForm<SiteFormValues>({
    resolver: yupResolver(siteSchema) as Resolver<SiteFormValues>,
    defaultValues: {
      pathSegment: '',
      authenticationConnectionId: undefined,
      wicketAuthEnabled: undefined,
      requirePersonNames: undefined,
      personFields: [],
      design: {
        introduction: {
          enabled: undefined,
          html: undefined,
        },
        campaignPageTitle: undefined,
        campaignPageDescriptionHtml: undefined,
        largeLogoImageId: null,
        largeLogoImageUrl: undefined,
        smallLogoImageId: null,
        smallLogoImageUrl: undefined,
        backgroundImageId: null,
        backgroundImageUrl: undefined,
        useWicketFooter: undefined,
        footerHtml: undefined,
        privacyPolicyHtml: undefined,
        privacyPolicyAgreementText: undefined,
        styleSheet: undefined,
      },
    },
  });

  useEffect(() => {
    if (site) {
      let connectionId = '';
      if (site.authenticationConnection) {
        if (
          typeof site.authenticationConnection === 'object' &&
          site.authenticationConnection !== null &&
          'id' in site.authenticationConnection
        ) {
          connectionId = site.authenticationConnection.id ?? '';
        } else if (typeof site.authenticationConnection === 'string') {
          connectionId = site.authenticationConnection;
        }
      }

      formMethods.reset({
        pathSegment: site.pathSegment ?? '',
        authenticationConnectionId: connectionId || undefined,
        wicketAuthEnabled: site.wicketAuthEnabled ?? undefined,
        requirePersonNames: site.requirePersonNames ?? undefined,
        personFields: toPersonFieldFormValues(site),
        design: {
          introduction: {
            enabled: site.design?.introduction?.enabled ?? undefined,
            html: site.design?.introduction?.html ?? undefined,
          },
          campaignPageTitle: site.design?.campaignPageTitle ?? undefined,
          campaignPageDescriptionHtml: site.design?.campaignPageDescriptionHtml ?? undefined,
          largeLogoImageId: site.design?.largeLogoImageId ?? null,
          largeLogoImageUrl: site.design?.largeLogoImageUrl ?? undefined,
          smallLogoImageId: site.design?.smallLogoImageId ?? null,
          smallLogoImageUrl: site.design?.smallLogoImageUrl ?? undefined,
          backgroundImageId: site.design?.backgroundImageId ?? null,
          backgroundImageUrl: site.design?.backgroundImageUrl ?? undefined,
          useWicketFooter: site.design?.useWicketFooter ?? undefined,
          footerHtml: site.design?.footerHtml ?? undefined,
          privacyPolicyHtml: site.design?.privacyPolicyHtml ?? undefined,
          privacyPolicyAgreementText: site.design?.privacyPolicyAgreementText ?? undefined,
          styleSheet: site.design?.styleSheet ?? undefined,
        },
      });
    }
  }, [site, formMethods]);

  const handleSave = async () => {
    if (site) {
      const result = await formMethods.handleSubmit((formValues) => {
        // personFields replaces the whole selection on the API, so it is sent in full rather than per dirty entry.
        const dirtyValues = extractDirtyValues(formMethods.formState.dirtyFields, formValues, [
          'design',
          'personFields',
        ]);
        if (!dirtyValues) return;

        const dataToSend = {
          ...dirtyValues,
          id: `${site.id}`,
          authenticationConnectionId:
            dirtyValues.authenticationConnectionId === null
              ? undefined
              : (dirtyValues.authenticationConnectionId ?? undefined),
          requirePersonNames:
            dirtyValues.requirePersonNames === null ? undefined : (dirtyValues.requirePersonNames ?? undefined),
          wicketAuthEnabled:
            dirtyValues.wicketAuthEnabled === null ? undefined : (dirtyValues.wicketAuthEnabled ?? undefined),
        };

        updateSite(dataToSend);
      })();

      return result;
    }
  };

  const handleNavigateBack = () => {
    navigateTo.sites();
  };

  const handleCancel = () => {
    if (site) {
      const connectionId =
        typeof site.authenticationConnection === 'object' &&
        site.authenticationConnection !== null &&
        'id' in site.authenticationConnection
          ? (site.authenticationConnection.id ?? '')
          : '';

      formMethods.reset({
        pathSegment: site.pathSegment ?? '',
        authenticationConnectionId: connectionId || undefined,
        wicketAuthEnabled: site.wicketAuthEnabled ?? undefined,
        requirePersonNames: site.requirePersonNames ?? undefined,
        personFields: toPersonFieldFormValues(site),
        design: {
          introduction: {
            enabled: site.design?.introduction?.enabled ?? undefined,
            html: site.design?.introduction?.html ?? undefined,
          },
          campaignPageTitle: site.design?.campaignPageTitle ?? undefined,
          campaignPageDescriptionHtml: site.design?.campaignPageDescriptionHtml ?? undefined,
          largeLogoImageId: site.design?.largeLogoImageId ?? null,
          largeLogoImageUrl: site.design?.largeLogoImageUrl ?? undefined,
          smallLogoImageId: site.design?.smallLogoImageId ?? null,
          smallLogoImageUrl: site.design?.smallLogoImageUrl ?? undefined,
          backgroundImageId: site.design?.backgroundImageId ?? null,
          backgroundImageUrl: site.design?.backgroundImageUrl ?? undefined,
          useWicketFooter: site.design?.useWicketFooter ?? undefined,
          footerHtml: site.design?.footerHtml ?? undefined,
          privacyPolicyHtml: site.design?.privacyPolicyHtml ?? undefined,
          privacyPolicyAgreementText: site.design?.privacyPolicyAgreementText ?? undefined,
          styleSheet: site.design?.styleSheet ?? undefined,
        },
      });
    }
  };

  const handleUpdateName = async (name: string) => {
    if (site?.id && name.length && site?.name !== name) {
      try {
        await new Promise((resolve, reject) => {
          updateSite(
            { name, id: `${site.id}` },
            {
              onSuccess: () => resolve(true),
              onError: (error) => reject(error),
            },
          );
        });
      } catch (error) {
        console.error('Error during name update:', error);
      }
    }
  };

  const handleTabChange = (key: string) => navigateTo.siteDetails(id, key);

  const value = {
    isLoading,
    site,
    error,
    formMethods,
    clientConnections,
    clientCustomPersonFields,
    authenticationConnections,
    updateSite,
    isUpdateSitePending,
    handleSave,
    clientAssets: clientAssets ?? [],
    isClientAssetsLoading,
    handleCancel,
    handleNavigateBack,
    handleUpdateName,
    handleTabChange,
  };

  return (
    <SiteDetailsContext.Provider value={value}>
      <Form layout="vertical" data-testid="site-details-form-provider">
        <FormProvider {...formMethods}>{children}</FormProvider>
      </Form>
    </SiteDetailsContext.Provider>
  );
};
